import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import {
    CheckSquare,
    Calendar,
    CalendarRange,
    Users,
    PieChart,
    LayoutDashboard,
    ChevronLeft,
    ChevronRight,
    Megaphone,
    Globe
} from 'lucide-react';


const Sidebar = ({ selectedDivision, onDivisionSelect }) => {
    const { user } = useAuth();
    const location = useLocation();
    const [isCollapsed, setIsCollapsed] = useState(false);

    // Helpers
    const getDivisionFromUrl = () => {
        // 1. Path param
        const pathMatch = location.pathname.match(/^\/division\/([^/]+)/);
        if (pathMatch) return pathMatch[1];

        // 2. Query param
        const searchParams = new URLSearchParams(location.search);
        return searchParams.get('division');
    };
    const currentDivision = getDivisionFromUrl();

    const getLink = (path) => {
        if (currentDivision && ['/tasks', '/events', '/gantt', '/event-planner'].includes(path)) {
            // Encode the division ID in the query param
            return `${path}?division=${encodeURIComponent(currentDivision)}`;
        }
        return path;
    };

    // Logic from Layout.js
    const groups = user?.roles || [];
    const isDGUser = groups.includes('DG') || groups.includes('DG_OFFICE');
    const isCoordinator = groups.some(r => r.startsWith('COORDINATOR_'));

    const menuItems = [
        { name: 'Dashboard', icon: <LayoutDashboard size={20} />, path: '/', color: '#3b82f6' }, // Blue
        { name: 'Tasks', icon: <CheckSquare size={20} />, path: '/tasks', color: '#10b981' },   // Green
        { name: 'Events', icon: <Megaphone size={20} />, path: '/events', color: '#f59e0b' },   // Amber
        { name: 'Event Planner', icon: <CalendarRange size={20} />, path: '/event-planner', color: '#10b981' }, // Emerald Green
        { name: 'Calendar', icon: <Calendar size={20} />, path: '/calendar', color: '#8b5cf6' }, // Violet
        ...(isDGUser || isCoordinator ? [{ name: 'Experts', icon: <Globe size={20} />, path: '/faculty', color: '#6366f1' }] : [])
    ];

    // Extra coordinator nav (shown only outside division context)
    const showCoordTeam = isCoordinator && !isDGUser && !currentDivision;


    const isActive = (path) => location.pathname === path;

    const linkStyle = (path) => ({
        display: 'flex',
        fontSize: '0.95rem',
        alignItems: 'center',
        justifyContent: isCollapsed ? 'center' : 'flex-start',
        gap: isCollapsed ? 0 : '0.85rem',
        padding: '0.85rem 1rem',
        color: isActive(path) ? '#ffffff' : '#1e293b',
        backgroundColor: isActive(path) ? 'rgba(15, 23, 42, 0.72)' : 'transparent',
        borderRadius: '12px', // Rounded corners
        textDecoration: 'none',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        fontWeight: isActive(path) ? 600 : 500,
        marginBottom: '0.5rem',
        boxShadow: isActive(path) ? '0 4px 6px rgba(0,0,0,0.1)' : 'none',
        position: 'relative',
        overflow: 'hidden',
        whiteSpace: 'nowrap'
    });

    return (
        <aside style={{
            width: isCollapsed ? '80px' : '260px', // Slightly wider for better spacing
            background: 'linear-gradient(155deg, #dbeafe 0%, #ede9fe 42%, #ccfbf1 100%)', // Mixed Task + Event gradient
            borderRight: '1px solid #a5b4fc',
            boxShadow: '0 12px 30px rgba(15, 23, 42, 0.22)',
            display: 'flex',
            flexDirection: 'column',
            padding: '1.5rem 1rem',
            overflowY: 'auto',
            height: '100%',
            transition: 'width 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
            position: 'relative',
            zIndex: 20,
            borderRadius: '24px',
            overflowX: 'visible'
        }}>
            {/* Toggle Button */}
            <button
                onClick={() => setIsCollapsed(!isCollapsed)}
                style={{
                    position: 'absolute',
                    top: '20px',
                    right: isCollapsed ? 'auto' : '-12px',
                    left: isCollapsed ? '50%' : 'auto',
                    transform: isCollapsed ? 'translateX(-50%)' : 'none',
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    background: '#1e293b', // Dark button
                    border: '1px solid #334155',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    color: '#94a3b8', // Light icon
                    zIndex: 50,
                    transition: 'all 0.3s'
                }}
            >
                {isCollapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
            </button>

            <nav style={{ display: 'flex', flexDirection: 'column', flex: 1, marginTop: '2rem' }}>

                {!isCollapsed && (
                    <div style={{
                        marginBottom: '0.75rem', paddingLeft: '0.75rem', fontSize: '0.65rem',
                        fontWeight: 'bold', textTransform: 'uppercase', color: '#334155', letterSpacing: '0.05em',
                        transition: 'opacity 0.2s'
                    }}>
                        Menu
                    </div>
                )}

                {menuItems.map(item => {
                    const active = isActive(item.path);
                    return (
                        <Link
                            key={item.name}
                            to={getLink(item.path)}
                            style={linkStyle(item.path)}
                            title={isCollapsed ? item.name : ""}
                        >
                            {/* Clone element to enforce color: White if active, Item Color if inactive */}
                            {React.cloneElement(item.icon, {
                                color: active ? '#ffffff' : item.color
                            })}
                            {!isCollapsed && <span style={{ opacity: isCollapsed ? 0 : 1, transition: 'opacity 0.2s' }}>{item.name}</span>}
                        </Link>
                    );
                })}

                {/* Coordinator Team Section - shown only outside division context */}
                {showCoordTeam && (
                    <>
                        <div style={{
                            margin: '1.5rem 0 0.5rem', paddingLeft: '0.75rem', fontSize: '0.65rem',
                            fontWeight: 'bold', textTransform: 'uppercase', color: '#334155', letterSpacing: '0.05em',
                            display: isCollapsed ? 'none' : 'block'
                        }}>
                            My Division
                        </div>
                        <Link to="/coordinator/team" style={linkStyle('/coordinator/team')} title={isCollapsed ? "Team" : ""}>
                            <Users size={20} color={isActive('/coordinator/team') ? '#ffffff' : '#06b6d4'} style={{ minWidth: '20px' }} />
                            {!isCollapsed && <span style={{ opacity: isCollapsed ? 0 : 1, transition: 'opacity 0.2s' }}>Team</span>}
                        </Link>
                    </>
                )}

                {/* DG / Admin Section - Only show if NOT in a division context */}
                {!currentDivision && isDGUser && (
                    <>

                        <div style={{
                            margin: '1.5rem 0 0.5rem', paddingLeft: '0.75rem', fontSize: '0.65rem',
                            fontWeight: 'bold', textTransform: 'uppercase', color: '#334155', letterSpacing: '0.05em',
                            display: isCollapsed ? 'none' : 'block', transition: 'opacity 0.2s'
                        }}>
                            Administration
                        </div>
                        {isCollapsed && <div style={{ height: '1rem' }} />}

                        <Link to="/dg/users" style={linkStyle('/dg/users')} title={isCollapsed ?
                            "Users" : ""}>
                            <Users size={20} color={isActive('/dg/users') ? '#ffffff' : '#06b6d4'} style={{ minWidth: '20px' }} />
                            {!isCollapsed && <span style={{ opacity: isCollapsed ? 0 : 1, transition: 'opacity 0.2s' }}>Team</span>}
                        </Link>
                        <Link to="/dg/analytics" style={linkStyle('/dg/analytics')} title={isCollapsed ? "Analytics" : ""}>
                            <PieChart size={20} color={isActive('/dg/analytics') ? '#ffffff' : '#ec4899'} style={{ minWidth: '20px' }} />
                            {!isCollapsed && <span style={{ opacity: isCollapsed ? 0 : 1, transition: 'opacity 0.2s' }}>Analytics</span>}
                        </Link>

                    </>
                )}
            </nav>

            <div style={{
                marginTop: '1rem',
                padding: isCollapsed ? '0.5rem 0.25rem' : '0.65rem 0.75rem',
                borderTop: '1px solid rgba(148, 163, 184, 0.28)',
                background: 'rgba(255, 255, 255, 0.04)',
                borderRadius: '12px',
                textAlign: 'center'
            }}>
                {!isCollapsed ? (
                    <div style={{ color: '#334155', fontSize: '0.74rem', fontWeight: 600, letterSpacing: '0.02em' }}>
                        RIS Dashboard v1.1
                    </div>
                ) : (
                    <div title="RIS Dashboard v1.1" style={{ color: '#334155', fontSize: '0.72rem', fontWeight: 700 }}>
                        v1.1
                    </div>
                )}
            </div>
        </aside>
    );
};

export default Sidebar;
